import { memo, useMemo } from 'react';

import { useTheme } from '@zenbusiness/zui/styles';
import PropTypes from 'prop-types';

import { COUNTERPARTY_CONNECTED_TYPE } from '#client/constants/counterParty';
import { capitalizeFirstLetter } from '#client/utils/text';

import AccountCommon from './AccountCommon';
import AccountCommonV2 from './AccountCommonV2';

function Account({
  name,
  last4Digits,
  numberOfCharacters = 2,
  handleSelect,
  connectedType,
  bankName,
  accountType,
  isSendMoney = false,
  isACHSupported,
  isWireSupported
}) {
  const theme = useTheme();
  const isPlaidType = connectedType === COUNTERPARTY_CONNECTED_TYPE.PLAID;
  const { subtitle, title } = useMemo(() => {
    if (isPlaidType) {
      return {
        title: `${bankName} ${last4Digits}`,
        subtitle: `${capitalizeFirstLetter(accountType)}`
      };
    }
    return {
      title: `${name} ${last4Digits}`,
      subtitle: `${bankName}`
    };
  }, [accountType, bankName, isPlaidType, last4Digits, name]);

  const statuses = useMemo(() => {
    const list = [];
    if (isACHSupported) {
      list.push({
        status: 'ACH',
        color: 'primary',
        bgColor: theme.palette.background.grey.dark
      });
    }

    if (isWireSupported) {
      list.push({
        status: 'Wire',
        color: 'primary',
        bgColor: theme.palette.background.grey.dark
      });
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isACHSupported, isWireSupported]);

  if (isSendMoney) {
    return (
      <AccountCommonV2
        buttonText="Select"
        onClick={handleSelect}
        statuses={statuses}
        subtitle={subtitle}
        title={title}
      />
    );
  }

  return (
    <AccountCommon
      disabled={false}
      loading={false}
      name={name}
      numberOfCharacters={numberOfCharacters}
      onClick={handleSelect}
      subtitle={subtitle}
      title={title}
    />
  );
}

Account.propTypes = {
  name: PropTypes.string.isRequired,
  last4Digits: PropTypes.string.isRequired,
  numberOfCharacters: PropTypes.number,
  handleSelect: PropTypes.func,
  connectedType: PropTypes.string,
  bankName: PropTypes.string,
  accountType: PropTypes.string,
  isSendMoney: PropTypes.bool,
  isACHSupported: PropTypes.bool,
  isWireSupported: PropTypes.bool
};

export default memo(Account);
